import React, { useEffect, useState, useMemo } from "react";
import {
  getMenuPage, submitMenuOrder,
  MenuPageData, MenuCategory,
} from "../../utils/api/menuOrdersAPI";
import { usePublicLang } from "../PublicApartments/PublicLangContext";
import { LangSwitcher } from "../PublicApartments/LangSwitcher";
import "./PublicMenu.scss";

// ─── Types ────────────────────────────────────────────────────────────────────

interface CartEntry { itemId: string; itemName: string; price: number; currency: string; quantity: number; key: string }
type Step = "category" | "menu" | "info" | "done";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MEAL_TYPE_ICON: Record<string, string> = {
  breakfast: "🍳", lunch: "🥗", dinner: "🍽️", snack: "🥪", other: "🍴",
};

const fmtPrice = (price: number, currency: string) => {
  if (currency === "EUR") return `€${price % 1 === 0 ? price : price.toFixed(2)}`;
  if (currency === "BAM") return `${price} KM`;
  return `${price} RSD`;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

interface StepperProps { label: string; icon: string; value: number; onChange: (v: number) => void; min?: number; max?: number; }
const Stepper: React.FC<StepperProps> = ({ label, icon, value, onChange, min = 1, max = 20 }) => (
  <div className="pm-stepper">
    <span className="pm-stepper__icon">{icon}</span>
    <span className="pm-stepper__label">{label}</span>
    <button className="pm-stepper__btn" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}>−</button>
    <span className="pm-stepper__val">{value}</span>
    <button className="pm-stepper__btn" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}>+</button>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const PublicMenu: React.FC = () => {
  const { t, lang } = usePublicLang();
  const aptId = window.location.pathname.split("/").filter(Boolean).pop() || "";

  const [data, setData] = useState<MenuPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [step, setStep] = useState<Step>("category");
  const [selectedCategory, setSelectedCategory] = useState<MenuCategory | null>(null);
  const [cart, setCart] = useState<CartEntry[]>([]);
  const [guestCount, setGuestCount] = useState(2);
  const [dayCount, setDayCount] = useState(1);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!aptId) { setError("Nedostaje ID apartmana"); setLoading(false); return; }
    getMenuPage(aptId)
      .then((res) => {
        const d = res.data;
        // Backwards compat: no categories → wrap suppliers' meals in a virtual "meals" category
        if (!d.categories || d.categories.length === 0) {
          if (d.suppliers && d.suppliers.length > 0) {
            const flatMeals = d.suppliers.flatMap((s) => s.meals);
            const virtual: MenuCategory = { _id: "__meal", name: "Hrana", emoji: "🍽️", type: "meals", meals: flatMeals, showPrices: true };
            d.categories = [virtual];
          }
        }
        setData(d);
        // Auto-enter if only one category
        if (d.categories && d.categories.length === 1) {
          enterCategory(d.categories[0]);
        }
      })
      .catch(() => setError("Greška pri učitavanju menija"))
      .finally(() => setLoading(false));
  }, [aptId]);

  // ── Category entry ─────────────────────────────────────────────────────────

  const enterCategory = (cat: MenuCategory) => {
    setSelectedCategory(cat);
    setCart([]);
    setStep("menu");
  };

  const goBackToCategories = () => {
    setStep("category");
    setSelectedCategory(null);
    setCart([]);
  };

  // ── Cart helpers ────────────────────────────────────────────────────────────

  const cartTotal = useMemo(
    () => cart.reduce((s, e) => s + e.price * e.quantity * dayCount, 0),
    [cart, dayCount]
  );
  const cartCurrency = cart[0]?.currency || selectedCategory?.currency || "EUR";
  const showPrices = selectedCategory?.showPrices !== false;

  const addItem = (itemId: string, itemName: string, price: number, currency: string, startQty?: number) => {
    const key = `${selectedCategory?._id}__${itemId}`;
    setCart((prev) => {
      const idx = prev.findIndex((e) => e.key === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      return [...prev, { itemId, itemName, price, currency, quantity: startQty ?? guestCount, key }];
    });
  };

  const removeItem = (key: string) => {
    setCart((prev) => {
      const idx = prev.findIndex((e) => e.key === key);
      if (idx < 0) return prev;
      const next = [...prev];
      if (next[idx].quantity > 1) next[idx] = { ...next[idx], quantity: next[idx].quantity - 1 };
      else next.splice(idx, 1);
      return next;
    });
  };

  const getQty = (itemId: string) =>
    cart.find((e) => e.key === `${selectedCategory?._id}__${itemId}`)?.quantity ?? 0;

  // Extras are optional add-ons (e.g. yogurt, cake) — simple on/off, not multiplied by guest count
  const toggleExtra = (itemId: string, itemName: string, price: number, currency: string) => {
    const key = `${selectedCategory?._id}__${itemId}`;
    setCart((prev) => {
      const idx = prev.findIndex((e) => e.key === key);
      if (idx >= 0) {
        const next = [...prev];
        next.splice(idx, 1);
        return next;
      }
      return [...prev, { itemId, itemName, price, currency, quantity: 1, key }];
    });
  };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!guestEmail.trim() || !guestPhone.trim()) { setFormError(lang === "en" ? "Email and phone number are required" : "Email i broj telefona su obavezni"); return; }
    if (!/\S+@\S+\.\S+/.test(guestEmail)) { setFormError(lang === "en" ? "Enter a valid email" : "Unesite validan email"); return; }
    setFormError("");
    setSubmitting(true);
    try {
      await submitMenuOrder(aptId, {
        guestEmail: guestEmail.trim(),
        guestPhone: guestPhone.trim(),
        categoryId: selectedCategory?._id,
        categoryName: selectedCategory?.name,
        items: cart.map((e) => ({ itemName: e.itemName, price: e.price, quantity: e.quantity * dayCount })),
        total: cartTotal,
        currency: cartCurrency,
        guests: guestCount,
        days: dayCount,
        note: note.trim() || undefined,
      });
      setStep("done");
    } catch {
      setFormError(lang === "en" ? "Error sending order. Please try again." : "Greška pri slanju narudžbe. Pokušajte ponovo.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading / Error ─────────────────────────────────────────────────────────

  const itemsLabel = lang === "en" ? "items" : "stavki";
  const continueLabel = lang === "en" ? "Continue →" : "Nastavi →";
  const dayLabel = (n: number) => lang === "en" ? (n === 1 ? "day" : "days") : (n === 1 ? "dan" : "dana");
  const guestLabel = (n: number) => lang === "en" ? (n === 1 ? "guest" : "guests") : (n === 1 ? "gost" : "gostiju");

  if (loading) return (
    <div className="pm-shell">
      <div className="pm-loading"><span className="pm-loading__icon">🍽️</span>{lang === "en" ? "Loading…" : "Učitavanje…"}</div>
    </div>
  );
  if (error || !data) return (
    <div className="pm-shell">
      <div className="pm-error"><span style={{ fontSize: 42 }}>🔗</span><div>{error || (lang === "en" ? "Menu not available" : "Meni nije dostupan")}</div></div>
    </div>
  );
  if (!data.categories || data.categories.length === 0) return (
    <div className="pm-shell">
      <div className="pm-header">
        <div className="pm-header__name">{data.apartment.name}</div>
        <LangSwitcher />
      </div>
      <div className="pm-error"><span style={{ fontSize: 38 }}>🛒</span><div>{lang === "en" ? "No services currently available" : "Trenutno nema dostupnih usluga"}</div></div>
    </div>
  );

  // ── Done ────────────────────────────────────────────────────────────────────

  if (step === "done") return (
    <div className="pm-shell">
      <div className="pm-header">
        <div className="pm-header__name">{data.apartment.name}</div>
        <LangSwitcher />
      </div>
      <div className="pm-done">
        <div className="pm-done__icon">✅</div>
        <div className="pm-done__title">{t("orderReceived")}</div>
        <div className="pm-done__sub">{lang === "en" ? <>We will contact you at <strong>{guestEmail}</strong> or <strong>{guestPhone}</strong>.</> : <>Kontaktiraćemo vas na <strong>{guestEmail}</strong> ili <strong>{guestPhone}</strong>.</>}</div>
        {cartTotal > 0 && <div className="pm-done__total">{t("total")}: <strong>{fmtPrice(cartTotal, cartCurrency)}</strong></div>}
      </div>
    </div>
  );

  // ── Step: Category list ──────────────────────────────────────────────────────

  if (step === "category") return (
    <div className="pm-shell">
      <div className="pm-header">
        <div className="pm-header__apt">{lang === "en" ? "How can we help?" : "Šta možemo za vas?"}</div>
        <div className="pm-header__name">{data.apartment.name}</div>
        <LangSwitcher />
      </div>
      <div className="pm-section-label">{lang === "en" ? "Select a service" : "Odaberite uslugu"}</div>
      <div className="pm-categories">
        {data.categories.map((cat) => {
          const isOrder = cat.type === "items" || cat.type === "meals";
          const isContact = cat.type === "contact" || !!cat.contactPhone;
          return (
            <button key={cat._id} className="pm-cat-card" onClick={() => enterCategory(cat)}>
              <span className="pm-cat-card__emoji">{cat.emoji}</span>
              <div className="pm-cat-card__body">
                <span className="pm-cat-card__name">{cat.name}</span>
                {cat.description && <span className="pm-cat-card__desc">{cat.description}</span>}
                <div className="pm-cat-card__badges">
                  {isContact && <span className="pm-cat-card__badge pm-cat-card__badge--contact">{lang === "en" ? "☎ Call us" : "☎ Pozovite"}</span>}
                  {isOrder && <span className="pm-cat-card__badge pm-cat-card__badge--order">{lang === "en" ? "Order online" : "Naručite online"}</span>}
                </div>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          );
        })}
      </div>
    </div>
  );

  // ── Step: Menu (category content — branches on type) ─────────────────────────

  if (step === "menu" && selectedCategory) {
    const showBack = data.categories.length > 1;

    // Contact-only category — no cart, just a call button
    if (selectedCategory.type === "contact") return (
      <div className="pm-shell">
        <div className="pm-header">
          <div className="pm-header__apt">
            {showBack && <button className="pm-back" onClick={goBackToCategories}>←</button>}
            {selectedCategory.emoji} {selectedCategory.name}
          </div>
          <div className="pm-header__name">{data.apartment.name}</div>
          <LangSwitcher />
        </div>
        {selectedCategory.description && (
          <div className="pm-cat-desc-banner">{selectedCategory.description}</div>
        )}
        <div className="pm-contact-block">
          <div className="pm-contact-block__label">{lang === "en" ? "Contact" : "Kontakt"}</div>
          <a className="pm-contact-btn" href={`tel:${selectedCategory.contactPhone}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.3 2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.06 6.06l.97-.88a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            {selectedCategory.contactLabel || (lang === "en" ? "Contact us" : "Kontaktirajte nas")}
          </a>
          <div className="pm-contact-block__phone">{selectedCategory.contactPhone}</div>
        </div>
      </div>
    );

    // Custom items category
    if (selectedCategory.type === "items") {
      const catItems = selectedCategory.items || [];
      const currency = selectedCategory.currency || "EUR";
      return (
        <div className="pm-shell">
          <div className="pm-header">
            <div className="pm-header__apt">
              {showBack && <button className="pm-back" onClick={goBackToCategories}>←</button>}
              {selectedCategory.emoji} {selectedCategory.name}
            </div>
            <div className="pm-header__name">{data.apartment.name}</div>
            <LangSwitcher />
          </div>
          {selectedCategory.description && (
            <div className="pm-cat-desc-banner">{selectedCategory.description}</div>
          )}
          {selectedCategory.contactPhone && (
            <a className="pm-contact-btn" href={`tel:${selectedCategory.contactPhone}`} style={{ margin: "0 16px 12px" }}>
              📞 {selectedCategory.contactLabel || (lang === "en" ? "Call us" : "Pozovite nas")}
            </a>
          )}
          <div className="pm-setup-bar">
            <Stepper label={lang === "en" ? "Guests" : "Gostiju"} icon="👥" value={guestCount} onChange={setGuestCount} min={1} max={20} />
            <div className="pm-setup-bar__divider" />
            <Stepper label={lang === "en" ? "Days" : "Dana"} icon="📅" value={dayCount} onChange={setDayCount} min={1} max={30} />
          </div>
          <div className="pm-meals" style={{ padding: 0 }}>
            <div className="pm-meal">
              <div className="pm-items">
                {catItems.map((item) => {
                  const itemId = item._id || item.name;
                  const qty = getQty(itemId);
                  return (
                    <div key={itemId} className="pm-item">
                      <div className="pm-item__info">
                        <span className="pm-item__name">{item.name}</span>
                        {item.description && <span className="pm-item__desc">{item.description}</span>}
                        {showPrices && item.price > 0 && (
                          <span className="pm-item__price">{fmtPrice(item.price, item.currency || currency)}</span>
                        )}
                      </div>
                      <div className="pm-item__ctrl">
                        {qty > 0 ? (
                          <>
                            <button className="pm-qty-btn pm-qty-btn--minus" onClick={() => removeItem(`${selectedCategory._id}__${itemId}`)}>−</button>
                            <span className="pm-qty">{qty}</span>
                            <button className="pm-qty-btn pm-qty-btn--plus" onClick={() => addItem(itemId, item.name, item.price, item.currency || currency)}>+</button>
                          </>
                        ) : (
                          <button className="pm-add-btn" onClick={() => addItem(itemId, item.name, item.price, item.currency || currency)}>+ {lang === "en" ? "Add" : "Dodaj"}</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {cart.length > 0 && (
            <div className="pm-cart-bar">
              <div className="pm-cart-bar__info">
                <span className="pm-cart-bar__count">{cart.reduce((s, e) => s + e.quantity, 0)} {lang === "en" ? "pcs" : "os."} × {dayCount} {dayLabel(dayCount)}</span>
                {showPrices && <span className="pm-cart-bar__total">{fmtPrice(cartTotal, cartCurrency)}</span>}
              </div>
              <button className="pm-cart-bar__btn" onClick={() => setStep("info")}>{continueLabel}</button>
            </div>
          )}
        </div>
      );
    }

    // Meals category (type === "meals")
    const meals = selectedCategory.meals || [];
    return (
      <div className="pm-shell">
        <div className="pm-header">
          <div className="pm-header__apt">
            {showBack && <button className="pm-back" onClick={goBackToCategories}>←</button>}
            {selectedCategory.emoji} {selectedCategory.name}
          </div>
          <div className="pm-header__name">{data.apartment.name}</div>
          <LangSwitcher />
        </div>
        {selectedCategory.description && (
          <div className="pm-cat-desc-banner">{selectedCategory.description}</div>
        )}
        {selectedCategory.contactPhone && (
          <a className="pm-contact-btn" href={`tel:${selectedCategory.contactPhone}`} style={{ margin: "0 16px 12px" }}>
            📞 {selectedCategory.contactLabel || (lang === "en" ? "Call us" : "Pozovite nas")}
          </a>
        )}

        <div className="pm-capacity">
          <div className="pm-capacity__row">
            <div className="pm-capacity__cell">
              <span className="pm-capacity__icon">👥</span>
              <div className="pm-capacity__info">
                <span className="pm-capacity__lbl">{lang === "en" ? "Guests" : "Gostiju"}</span>
                <span className="pm-capacity__num">{guestCount}</span>
              </div>
              <div className="pm-capacity__btns">
                <button className="pm-capacity__btn" onClick={() => setGuestCount(Math.max(1, guestCount - 1))} disabled={guestCount <= 1}>−</button>
                <button className="pm-capacity__btn" onClick={() => setGuestCount(Math.min(20, guestCount + 1))} disabled={guestCount >= 20}>+</button>
              </div>
            </div>
            <div className="pm-capacity__times">×</div>
            <div className="pm-capacity__cell">
              <span className="pm-capacity__icon">📅</span>
              <div className="pm-capacity__info">
                <span className="pm-capacity__lbl">{lang === "en" ? "Days" : "Dana"}</span>
                <span className="pm-capacity__num">{dayCount}</span>
              </div>
              <div className="pm-capacity__btns">
                <button className="pm-capacity__btn" onClick={() => setDayCount(Math.max(1, dayCount - 1))} disabled={dayCount <= 1}>−</button>
                <button className="pm-capacity__btn" onClick={() => setDayCount(Math.min(30, dayCount + 1))} disabled={dayCount >= 30}>+</button>
              </div>
            </div>
          </div>
          <div className="pm-capacity__hint">
            {lang === "en"
              ? `Select up to ${guestCount} portion${guestCount !== 1 ? "s" : ""} per meal${dayCount > 1 ? ` · ${dayCount} days` : ""}`
              : `Birajte do ${guestCount} ${guestCount === 1 ? "porciju" : "porcije"} po obroku${dayCount > 1 ? ` · ${dayCount} dana` : ""}`}
          </div>
        </div>

        <div className="pm-meals">
          {meals.map((meal) => {
            const mealQty = meal.items.reduce((s, it) => s + getQty(it._id || it.name), 0);
            const isMealFull = mealQty >= guestCount;
            return (
              <div key={meal._id} className={`pm-meal${isMealFull ? " pm-meal--full" : ""}`}>
                <div className="pm-meal__head">
                  <span>{MEAL_TYPE_ICON[meal.type] || "🍴"}</span>
                  <div style={{ flex: 1 }}>
                    <span className="pm-meal__name">{meal.name}</span>
                    {meal.description && <div className="pm-meal__desc">{meal.description}</div>}
                    {meal.supplierNote && <div className="pm-meal__desc">{meal.supplierNote}</div>}
                    {meal.includedInPrice && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#16a34a", background: "rgba(22,163,74,0.1)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 12, padding: "1px 7px", marginTop: 2, display: "inline-block" }}>
                        ✓ {lang === "en" ? "Included in price" : "Uključeno u cijenu"}
                      </span>
                    )}
                    {(meal.orderingRules || []).length > 0 && (
                      <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 3 }}>
                        {meal.orderingRules!.map((rule, i) => (
                          <div key={i} style={{ fontSize: 11, color: "#6b6880", display: "flex", alignItems: "flex-start", gap: 5 }}>
                            <span style={{ color: "#9893a8", flexShrink: 0, marginTop: 1 }}>ℹ</span>
                            <span>{rule}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={`pm-meal__quota${isMealFull ? " pm-meal__quota--full" : mealQty > 0 ? " pm-meal__quota--partial" : ""}`}>
                    <span className="pm-meal__quota-num">{mealQty}</span>
                    <span className="pm-meal__quota-sep">/</span>
                    <span className="pm-meal__quota-max">{guestCount}</span>
                    <span className="pm-meal__quota-icon">👥</span>
                  </div>
                </div>
                <div className="pm-items">
                  {meal.items.map((item) => {
                    const itemId = item._id || item.name;
                    const qty = getQty(itemId);
                    const othersQty = mealQty - qty;
                    const remaining = guestCount - othersQty;
                    return (
                      <div key={itemId} className="pm-item">
                        <div className="pm-item__info">
                          <span className="pm-item__name">{item.name}</span>
                          {item.description && <span className="pm-item__desc">{item.description}</span>}
                          {showPrices && !meal.includedInPrice && item.price > 0 && (
                            <span className="pm-item__price">{fmtPrice(item.price, meal.currency)}</span>
                          )}
                          {meal.includedInPrice && (
                            <span className="pm-item__price" style={{ color: "#16a34a" }}>
                              {lang === "en" ? "included" : "uključeno"}
                            </span>
                          )}
                        </div>
                        <div className="pm-item__ctrl">
                          {qty > 0 ? (
                            <>
                              <button className="pm-qty-btn pm-qty-btn--minus" onClick={() => removeItem(`${selectedCategory._id}__${itemId}`)}>−</button>
                              <span className="pm-qty">{qty}</span>
                              <button className="pm-qty-btn pm-qty-btn--plus" disabled={qty >= remaining} onClick={() => addItem(itemId, item.name, meal.includedInPrice ? 0 : item.price, meal.currency)}>+</button>
                            </>
                          ) : (
                            <button className="pm-add-btn" disabled={isMealFull} onClick={() => addItem(itemId, item.name, meal.includedInPrice ? 0 : item.price, meal.currency, Math.min(guestCount, remaining))}>+ {lang === "en" ? "Add" : "Dodaj"}</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {(meal.extras || []).length > 0 && (
                  <div className="pm-extras">
                    <div className="pm-extras__label">{lang === "en" ? "Add-ons" : "Dodatne stavke"}</div>
                    {meal.extras!.map((extra) => {
                      const extraId = extra._id || extra.name;
                      const checked = getQty(extraId) > 0;
                      return (
                        <div
                          key={extraId}
                          className="pm-extra"
                          onClick={() => toggleExtra(extraId, extra.name, meal.includedInPrice ? 0 : extra.price, meal.currency)}
                        >
                          <span className={`pm-extra__box${checked ? " pm-extra__box--checked" : ""}`}>
                            {checked && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                            )}
                          </span>
                          <div className="pm-extra__info">
                            <span className="pm-extra__name">{extra.name}</span>
                            {extra.description && <span className="pm-item__desc">{extra.description}</span>}
                          </div>
                          {showPrices && !meal.includedInPrice && extra.price > 0 && (
                            <span className="pm-extra__price">{fmtPrice(extra.price, meal.currency)}</span>
                          )}
                          {meal.includedInPrice && (
                            <span className="pm-extra__price" style={{ color: "#16a34a" }}>
                              {lang === "en" ? "included" : "uključeno"}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {cart.length > 0 && (
          <div className="pm-cart-bar">
            <div className="pm-cart-bar__info">
              <span className="pm-cart-bar__count">{cart.reduce((s, e) => s + e.quantity, 0)} {lang === "en" ? "pcs" : "os."} × {dayCount} {dayLabel(dayCount)}</span>
              {showPrices && <span className="pm-cart-bar__total">{fmtPrice(cartTotal, cartCurrency)}</span>}
            </div>
            <button className="pm-cart-bar__btn" onClick={() => setStep("info")}>{continueLabel}</button>
          </div>
        )}
      </div>
    );
  }

  // ── Step: Checkout ───────────────────────────────────────────────────────────

  return (
    <div className="pm-shell">
      <div className="pm-header">
        <div className="pm-header__apt">
          <button className="pm-back" onClick={() => setStep("menu")}>←</button>
          {lang === "en" ? "Your details" : "Vaši podaci"}
        </div>
        <div className="pm-header__name">{data.apartment.name}</div>
        <LangSwitcher />
      </div>

      <div className="pm-summary">
        <div className="pm-section-label" style={{ paddingTop: 0, marginBottom: 10 }}>{lang === "en" ? "Order summary" : "Pregled narudžbe"}</div>
        {selectedCategory && (
          <div style={{ fontSize: 12, color: "#9893a8", marginBottom: 8 }}>
            {selectedCategory.emoji} {selectedCategory.name}
          </div>
        )}
        {dayCount > 1 && (
          <div style={{ fontSize: 12, color: "#7c6ef5", background: "#f5f3ff", padding: "6px 10px", borderRadius: 7, marginBottom: 8 }}>
            👥 {guestCount} {guestLabel(guestCount)} × 📅 {dayCount} {dayLabel(dayCount)}
          </div>
        )}
        {cart.map((e) => (
          <div key={e.key} className="pm-summary-row">
            <span className="pm-summary-row__name">
              {e.itemName}
              <span className="pm-summary-row__qty"> × {e.quantity}{dayCount > 1 ? ` × ${dayCount}d` : ""}</span>
            </span>
            {showPrices && <span className="pm-summary-row__price">{fmtPrice(e.price * e.quantity * dayCount, e.currency)}</span>}
          </div>
        ))}
        {showPrices && (
          <div className="pm-summary-total">
            <span>{t("total")}</span>
            <strong>{fmtPrice(cartTotal, cartCurrency)}</strong>
          </div>
        )}
      </div>

      <div className="pm-form">
        <div className="pm-section-label" style={{ paddingTop: 0, marginBottom: 12 }}>{lang === "en" ? "Contact details" : "Kontakt podaci"}</div>
        <div className="pm-field">
          <label className="pm-label">Email <span className="pm-req">*</span></label>
          <input className="pm-input" type="email" placeholder="vas@email.com" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} />
        </div>
        <div className="pm-field">
          <label className="pm-label">{lang === "en" ? "Phone number" : "Broj telefona"} <span className="pm-req">*</span></label>
          <input className="pm-input" type="tel" placeholder="+387 6x xxx xxx" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} />
        </div>
        <div className="pm-field">
          <label className="pm-label">{t("note")} <span className="pm-opt">{lang === "en" ? "optional" : "opciono"}</span></label>
          <textarea className="pm-input pm-input--textarea" placeholder={lang === "en" ? "Special requests…" : "Posebni zahtjevi…"} rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        {formError && <div className="pm-form-error">{formError}</div>}
        <button className="pm-submit-btn" onClick={handleSubmit} disabled={submitting || cart.length === 0}>
          {submitting ? (lang === "en" ? "Sending…" : "Slanje…") : showPrices ? `${lang === "en" ? "Order" : "Naruči"} — ${fmtPrice(cartTotal, cartCurrency)}` : t("sendOrder")}
        </button>
        <p className="pm-form-note">{lang === "en" ? "We will contact you regarding delivery at the provided email or phone." : "Kontaktiraćemo vas u vezi dostave na navedeni email ili telefon."}</p>
      </div>
    </div>
  );
};

export default PublicMenu;
